/**
 * API client for the console-worker. Phase 0 stubs return mocks so
 * the UI shell can render without depending on the backend being
 * deployed yet. Phase 1 wires real producers.
 *
 * Every fetch is wrapped so we can later add:
 *  - retry / backoff
 *  - tanstack-query caching
 *  - JWT pass-through (CF Access cookie is forwarded automatically
 *    on same-origin requests; no manual header needed)
 *
 * Toggle USE_MOCK = false once the worker is live.
 */
import type {
  MacStatus,
  VpsStatus,
  WorkersStatus,
  AnthropicSpendDay,
  TodaysSlateSummary,
  AnomalyBadges,
  ActivityEvent,
  ScheduleEntry,
  AuthIdentity,
  PipelineGraph,
  CalibrationSnapshot,
  CalibrationHistory,
  BetsResponse,
  CostSpendSnapshot,
  PromptIndex,
  SnapshotsIndex,
  SnapshotPayload,
  HrRotation,
  AnomalyEventLog,
  TodosIndex,
  MorningBriefLatest,
  EasterEggEventsAgg,
  ActiveEgg,
  LaunchAgentLogTail,
  LaunchAgentLogIndex,
  DashboardDiff,
} from "./types";

// Phase 1.C: console-worker is deployed at console.thekitchen.cloud/api/*
// with real R2 read-through endpoints. Mocks remain in this file as a
// fallback when the worker / its data sources are unreachable.
const USE_MOCK = false;

const API_BASE = "/api";

async function get<T>(path: string, mock: () => T): Promise<T> {
  if (USE_MOCK) return mock();
  console.log(`[api] GET ${API_BASE}${path}`);
  let r: Response;
  try {
    r = await fetch(`${API_BASE}${path}`, {
      credentials: "include",
      redirect: "error",   // surface CF Access redirects as fetch errors
                            // instead of silently following them to the
                            // login page and then failing on JSON parse
    });
  } catch (e) {
    console.error(`[api] ${path} fetch threw:`, e);
    const msg = e instanceof Error ? e.message : String(e);
    // A redirect-error here almost always means the CF Access cookie
    // expired or never set — point the user at the fix.
    if (/redirect/i.test(msg)) {
      throw new Error(`auth redirect on ${path}: CF Access session expired — reload page to re-authenticate`);
    }
    throw new Error(`fetch threw on ${path}: ${msg}`);
  }
  if (!r.ok) {
    const body = await r.text().catch(() => "<unreadable>");
    console.error(`[api] ${path} → ${r.status} ${r.statusText} body=${body.slice(0, 200)}`);
    throw new Error(`${path} → ${r.status}: ${body.slice(0, 100)}`);
  }
  const text = await r.text();
  try {
    const parsed = JSON.parse(text) as T;
    console.log(`[api] ${path} → ${r.status} (${text.length}B parsed)`);
    return parsed;
  } catch (e) {
    console.error(`[api] ${path} JSON parse failed; body preview:`, text.slice(0, 200));
    throw new Error(`${path}: parse failed: ${e instanceof Error ? e.message : String(e)}`);
  }
}

// =============================================================
// Endpoints
// =============================================================
export const api = {
  whoami: () => get<AuthIdentity>("/whoami", mockAuth),
  macStatus: () => get<MacStatus>("/mac-status", mockMacStatus),
  vpsStatus: () => get<VpsStatus>("/vps-status", mockVpsStatus),
  workersStatus: () => get<WorkersStatus>("/workers-status", mockWorkersStatus),
  anthropicSpend: (date?: string) =>
    get<AnthropicSpendDay>(`/anthropic-spend${date ? `?date=${date}` : ""}`,
                            mockSpend),
  todaysSlate: () => get<TodaysSlateSummary>("/todays-slate", mockSlate),
  anomalies: () => get<AnomalyBadges>("/anomalies", mockAnomalies),
  activity: (limit = 50) =>
    get<ActivityEvent[]>(`/activity?limit=${limit}`, () => mockActivity(limit)),
  schedule: () => get<ScheduleEntry[]>("/schedule", mockSchedule),
  pipelineGraph: () => get<PipelineGraph>("/pipeline-graph", mockPipelineGraph),
  calibration:   () => get<CalibrationSnapshot>("/calibration", mockCalibration),
  calibrationHistory: (days = 30) =>
    get<CalibrationHistory>(`/calibration-history?days=${days}`, () => ({
      dates: [], snapshots: [], generated_at: new Date().toISOString(),
    })),
  bets:          (limit = 1000) =>
    get<BetsResponse>(`/bets?limit=${limit}`, mockBets),
  costSpend:     (days = 30) =>
    get<CostSpendSnapshot>(`/anthropic-spend?days=${days}`, mockCostSpend),
  promptsIndex:  () => get<PromptIndex>("/prompts", mockPromptsIndex),
  snapshotsIndex: () => get<SnapshotsIndex>("/snapshots", mockSnapshotsIndex),
  snapshotAt:    (at: string, source = "mac-status") =>
    get<SnapshotPayload>(`/snapshot?at=${encodeURIComponent(at)}&source=${source}`,
                         () => ({ source, at, resolved_iso: at, payload: null })),
  hrRotation:    () => get<HrRotation>("/hr-rotation", mockHrRotation),
  anomalyEvents: (limit = 200) =>
    get<AnomalyEventLog>(`/anomaly-events?limit=${limit}`, mockAnomalyEvents),
  todos:         () => get<TodosIndex>("/todos", mockTodos),
  activeEggs:    () => get<ActiveEgg[]>("/active-eggs", () => []),

  launchagentLog: (label: string, lines = 200) =>
    get<LaunchAgentLogTail>(
      `/launchagent-log?label=${encodeURIComponent(label)}&lines=${lines}`,
      () => ({ label, log: "", bytes: 0, total_lines: 0,
                returned_lines: 0, fetched_at: new Date().toISOString() }),
    ),
  launchagentLogIndex: () =>
    get<LaunchAgentLogIndex>("/launchagent-log-index",
      () => ({ generated_at: "", count: 0, agents: [] })),

  async launchagentTrigger(label: string): Promise<{ id: string; status: string }> {
    const r = await fetch("/api/launchagent-trigger", {
      method: "POST",
      credentials: "include",
      headers: { "content-type": "application/json" },
      body: JSON.stringify({ label }),
      redirect: "error",
    });
    if (!r.ok) throw new Error(`trigger failed: ${r.status}`);
    return r.json();
  },
  launchagentTriggerStatus: (id: string) =>
    get<{ id: string; status: "pending" | "done";
           result?: { ok: boolean; exit_code: number | null;
                       stderr: string; executed_at: string } }>(
      `/launchagent-trigger?id=${encodeURIComponent(id)}`,
      () => ({ id, status: "pending" }),
    ),

  dashboardSnapshots: () =>
    get<{ dates: string[]; note?: string }>("/dashboard-snapshots",
      () => ({ dates: [] })),
  dashboardSnapshot: (date: string) =>
    get<{ date: string; snapshot: any; error?: string }>(
      `/dashboard-snapshot?date=${encodeURIComponent(date)}`,
      () => ({ date, snapshot: null, error: "no data" }),
    ),
  dashboardDiff: (a: string, b: string) =>
    get<DashboardDiff>(
      `/dashboard-diff?a=${encodeURIComponent(a)}&b=${encodeURIComponent(b)}`,
      () => ({ a, b, changes: [], sport_summary: {}, total_changes: 0,
                truncated: false }),
    ),

  async eggUpload(payload: {
    id: string; name: string; expires_at: string;
    animation: "slide-up"; image_b64: string;
  }): Promise<{ ok: boolean; descriptor: ActiveEgg; total_active: number }> {
    const r = await fetch("/api/eggs/upload", {
      method: "POST",
      credentials: "include",
      headers: { "content-type": "application/json" },
      body: JSON.stringify(payload),
      redirect: "error",
    });
    if (!r.ok) {
      const body = await r.text().catch(() => "");
      throw new Error(`/api/eggs/upload → ${r.status}: ${body.slice(0, 200)}`);
    }
    return r.json();
  },

  async eggRetract(id: string): Promise<{ ok: boolean; total_active: number }> {
    const r = await fetch("/api/eggs/retract", {
      method: "POST",
      credentials: "include",
      headers: { "content-type": "application/json" },
      body: JSON.stringify({ id }),
      redirect: "error",
    });
    if (!r.ok) {
      const body = await r.text().catch(() => "");
      throw new Error(`/api/eggs/retract → ${r.status}: ${body.slice(0, 200)}`);
    }
    return r.json();
  },

  eggEvents:     () => get<EasterEggEventsAgg>("/easter-egg-events", () => ({
                           total: 0, counts: {}, daily_by_egg: {},
                           dates: [], recent: [],
                           generated_at: new Date().toISOString(),
                         })),
  morningBrief:  () => get<MorningBriefLatest>("/morning-brief", () => ({
                          date: "", generated_at: "",
                          input_tokens: 0, output_tokens: 0, cost_usd: 0,
                          n_anomalies_24h: 0, n_bets_yesterday: 0,
                          has_top_miss: false,
                          error: "no_briefs_yet",
                        })),

  // Force-rerun the morning brief on demand.
  async morningBriefRerun(): Promise<MorningBriefLatest> {
    const r = await fetch("/api/morning-brief", {
      method: "POST",
      credentials: "include",
      headers: { "content-type": "application/json" },
      body: JSON.stringify({ force: true }),
      redirect: "error",
    });
    if (!r.ok) {
      const body = await r.text().catch(() => "");
      throw new Error(`morning-brief rerun → ${r.status}: ${body.slice(0, 200)}`);
    }
    return r.json();
  },

  // Prompt body fetch — returns raw text/plain. Bypasses the JSON
  // wrapper because the worker route returns plain text.
  async promptBody(path: string): Promise<string> {
    const r = await fetch(`/api/prompt?path=${encodeURIComponent(path)}`, {
      credentials: "include",
      redirect: "error",
    });
    if (!r.ok) {
      const body = await r.text().catch(() => "");
      throw new Error(`/api/prompt → ${r.status}: ${body.slice(0, 100)}`);
    }
    return r.text();
  },
};

// =============================================================
// Mock data — realistic shapes so the UI looks "real" in Phase 0
// =============================================================
function mockAuth(): AuthIdentity {
  return {
    email: "ysankov1@gmail.com",
    name: "Yury",
    signed_in_via: "dev-passthrough",
  };
}

function mockMacStatus(): MacStatus {
  const now = new Date().toISOString();
  return {
    generated_at: now,
    hostname: "kitchen-mac",
    disk_free_gb: 482.3,
    memory_free_gb: 18.6,
    load_avg: [0.82, 0.91, 1.04],
    launchagents: MOCK_LAUNCHAGENTS,
    recent_commits: MOCK_COMMITS,
  };
}

function mockVpsStatus(): VpsStatus {
  return {
    generated_at: new Date().toISOString(),
    hostname: "kitchen-vps",
    last_scrape_at: new Date(Date.now() - 4 * 60_000).toISOString(),
    cadence: "off-peak",
    skip_reason: null,
    disk_free_gb: 38.1,
    memory_free_gb: 1.4,
    sport_freshness: { nba: 240, wnba: 240, mlb: 240, nhl: 240, soccer: 240, ufc: 240 },
    recent_errors: [],
    bytes_uploaded_24h: 1_247_392_055,
  };
}

function mockWorkersStatus(): WorkersStatus {
  return {
    deployed: 1,
    workers: [{
      name: "parlay-scorer",
      role: "HR catalog cron + grader",
      alive: true,
      last_pass_at: new Date(Date.now() - 45_000).toISOString(),
      age_seconds: 45,
      duration_ms: 2400,
      events_scanned: 100,
      events_failed: 0,
      new_selections: 6,
      refreshed: 18500,
      total_after: 33800,
      consecutive_429: 0,
      aborted_early: false,
      rolling: {
        passes: 60, total_new: 3200, total_refreshed: 1_100_000, total_failed: 0,
      },
      history: [],
    }],
    cost: {
      base_cost_usd: 5, overage_usd: 0, total_estimated_usd: 5,
      free_tier_included: 10_000_000, projected_monthly: 5_011_200,
    },
    generated_at: new Date().toISOString(),
  };
}

function mockSpend(): AnthropicSpendDay {
  return {
    date: new Date().toISOString().slice(0, 10),
    total_usd: 11.42,
    by_pipeline: {
      "8am-picker":           4.18,
      "soccer-fixture-fires": 2.10,
      "graders":              3.84,
      "calibration":          0.91,
      "ufc-picker":           0.39,
    },
    by_sport: { NBA: 1.92, WNBA: 0.74, MLB: 3.41, NHL: 0.62, SOCCER: 4.31, UFC: 1.08, OTHER: 0.08 },
    cache_hit_rate: 0.78,
    totals: { input_tokens: 3_412_009, output_tokens: 482_311, requests: 198 },
  };
}

function mockSlate(): TodaysSlateSummary {
  return {
    date: new Date().toISOString().slice(0, 10),
    by_sport: [
      { sport: "NBA",    posted: 24, graded: 0,  pending: 24, pnl_units: null },
      { sport: "WNBA",   posted: 12, graded: 0,  pending: 12, pnl_units: null },
      { sport: "MLB",    posted: 28, graded: 4,  pending: 24, pnl_units: 1.2 },
      { sport: "NHL",    posted: 3,  graded: 0,  pending: 3,  pnl_units: null },
      { sport: "NFL",    posted: 2,  graded: 0,  pending: 2,  pnl_units: 0 },
      { sport: "SOCCER", posted: 34, graded: 12, pending: 22, pnl_units: 0.4 },
      { sport: "UFC",    posted: 26, graded: 0,  pending: 26, pnl_units: null },
    ],
    count_sparkline: [82, 41, 73, 116, 98, 41, 115],
  };
}

function mockAnomalies(): AnomalyBadges {
  return {
    generated_at: new Date().toISOString(),
    badges: [
      {
        id: "stale-bet-id-mlb",
        severity: "warn",
        title: "1 stale hr_bet_id",
        detail: "CHW@SEA UNDER 8.5 — HR doesn't currently offer this line",
        link: "#bet-inspector?id=2026-05-09-139",
      },
    ],
  };
}

function mockActivity(limit: number): ActivityEvent[] {
  const now = Date.now();
  const seed = [
    { c: "scrape" as const,    msg: "VPS scrape complete (5min cycle)", ago: 60_000 },
    { c: "sync" as const,      msg: "sync_dashboard pushed", ago: 90_000 },
    { c: "outcome" as const,   msg: "MLB CIN @ HOU UNDER 9.5 → loss", ago: 6 * 60_000 },
    { c: "picker" as const,    msg: "Soccer fixture refire: Liverpool vs Chelsea (T-12min)", ago: 12 * 60_000 },
    { c: "grader" as const,    msg: "Soccer fixture: 2 picks scored", ago: 12 * 60_000 + 4_000 },
    { c: "deploy" as const,    msg: "dashboard-site → main bc98aa7", ago: 14 * 60_000 },
    { c: "scrape" as const,    msg: "VPS scrape complete (5min cycle)", ago: 19 * 60_000 },
    { c: "outcome" as const,   msg: "MLB PHI @ COL OVER 7.5 → win", ago: 28 * 60_000 },
  ];
  return seed.slice(0, limit).map((s, i) => ({
    id: `evt-${now}-${i}`,
    ts: new Date(now - s.ago).toISOString(),
    category: s.c,
    message: s.msg,
  }));
}

function mockSchedule(): ScheduleEntry[] {
  return [
    {
      agent_label: "com.yurysankov.hr-scrape",
      display_name: "HR scrape (overnight rollover)",
      schedule: "01:00 AM ET daily",
      category: "scrape",
      fires_next_24h: [],
      fires_prev_24h: [],
    },
    {
      agent_label: "com.yurysankov.nba-ai-picker",
      display_name: "Daily picker (NBA / MLB / NHL / SOCCER)",
      schedule: "08:00 AM ET daily",
      category: "picker",
      fires_next_24h: [],
      fires_prev_24h: [],
    },
    {
      agent_label: "com.yurysankov.ufc-picker",
      display_name: "UFC picker (self-gating, T-3 + T-1 days)",
      schedule: "06:00 AM ET, gates on next card within 4d",
      category: "picker",
      fires_next_24h: [],
      fires_prev_24h: [],
    },
    {
      agent_label: "com.yurysankov.dashboard-sync",
      display_name: "dashboard.json rebuild + push",
      schedule: "every 5 min",
      category: "sync",
      fires_next_24h: [],
      fires_prev_24h: [],
    },
    {
      agent_label: "com.yurysankov.soccer-fixture-scheduler",
      display_name: "Soccer per-fixture T-60 scheduler",
      schedule: "every 5 min",
      category: "picker",
      fires_next_24h: [],
      fires_prev_24h: [],
    },
    {
      agent_label: "com.yurysankov.mlb-probables-refresh",
      display_name: "MLB probables refresh",
      schedule: "5:30 AM, 1:00 PM, 5:00 PM ET",
      category: "probables",
      fires_next_24h: [],
      fires_prev_24h: [],
    },
    {
      agent_label: "com.yurysankov.nhl-probables-refresh",
      display_name: "NHL probables refresh",
      schedule: "5:30 AM, 1:00 PM, 5:00 PM ET",
      category: "probables",
      fires_next_24h: [],
      fires_prev_24h: [],
    },
  ];
}

const MOCK_LAUNCHAGENTS = [
  "com.yurysankov.hr-scrape",
  "com.yurysankov.nba-ai-picker",
  "com.yurysankov.ufc-picker",
  "com.yurysankov.dashboard-sync",
  "com.yurysankov.soccer-fixture-scheduler",
  "com.yurysankov.mlb-probables-refresh",
  "com.yurysankov.nhl-probables-refresh",
  "com.yurysankov.builder-grading",
  "com.yurysankov.nba-injury-refresh",
  "com.yurysankov.mlb-injury-refresh",
  "com.yurysankov.nhl-injury-refresh",
].map((label, i) => ({
  label,
  loaded: true,
  last_exit_code: 0 as number | null,
  next_fire: new Date(Date.now() + (i + 1) * 60 * 60_000).toISOString(),
  last_fire: new Date(Date.now() - (i + 1) * 30 * 60_000).toISOString(),
  log_path: `/tmp/${label.replace(/^com\.yurysankov\./, "")}.log`,
  schedule_label: i === 0 ? "01:00 AM ET" : i < 3 ? "06:00–08:00 AM ET" : "every 5 min",
}));

const MOCK_COMMITS: GitCommit[] = [
  {
    repo: "sports-betting-research",
    sha: "8eef179000000000000000000000000000000000",
    short_sha: "8eef179",
    message: "CAL-2026-05-09: exclude UFC from headline calibration + yesterday recap",
    author: "Yury Sankov",
    iso_date: new Date(Date.now() - 14 * 60_000).toISOString(),
    phase_tag: "CAL-2026-05-09",
  },
  {
    repo: "sports-betting-research",
    sha: "6f815e4000000000000000000000000000000000",
    short_sha: "6f815e4",
    message: "LIVE-2026-05-09.B: stale-file filter for hr_bet_id resolvers",
    author: "Yury Sankov",
    iso_date: new Date(Date.now() - 38 * 60_000).toISOString(),
    phase_tag: "LIVE-2026-05-09.B",
  },
  {
    repo: "sports-betting-research",
    sha: "8a773a9000000000000000000000000000000000",
    short_sha: "8a773a9",
    message: "SOC.bridge.4: surface soccer bets on the dashboard",
    author: "Yury Sankov",
    iso_date: new Date(Date.now() - 3.2 * 60 * 60_000).toISOString(),
    phase_tag: "SOC.bridge.4",
  },
];

// Re-export GitCommit for the import above
import type { GitCommit } from "./types";

function mockPipelineGraph(): PipelineGraph {
  return {
    generated_at: new Date().toISOString(),
    nodes: [
      { id: "hr-cdn",       label: "Hard Rock\nodds API", kind: "external", status: "ok" },
      { id: "vps-scraper",  label: "VPS scraper\nprime",  kind: "scraper",  status: "ok" },
      { id: "r2-snapshots", label: "R2",                  kind: "store",    status: "ok" },
      { id: "console-app",  label: "console.thekitchen.cloud", kind: "ui",  status: "ok" },
    ],
    edges: [
      { id: "e1", source: "hr-cdn",       target: "vps-scraper",  flowing: true,  kind: "data" },
      { id: "e2", source: "vps-scraper",  target: "r2-snapshots", flowing: true,  kind: "data" },
      { id: "e3", source: "r2-snapshots", target: "console-app",  flowing: false, kind: "data" },
    ],
  };
}

function mockCalibration(): CalibrationSnapshot {
  return {
    as_of: "2026-05-09",
    window_days: 30,
    n_decided: 286, wins: 157, losses: 129, pnl_units: 11.0,
    health: "yellow",
    sports: [
      { id: "all", n_decided: 286, pnl_units: 11.0, health: "yellow" },
      { id: "NBA", n_decided: 113, pnl_units: 9.09, health: "yellow" },
      { id: "WNBA", n_decided: 12, pnl_units: -0.3, health: "yellow" },
      { id: "MLB", n_decided: 159, pnl_units: 1.4, health: "yellow" },
    ],
    buckets: ["90+", "70-89", "50-69", "<50"],
    heatmap: [
      { sport: "all",  bucket: "70-89", delta_pp: -25.5, hit_rate: 54.0, decided: 87,  hits: 47,  ok: false },
      { sport: "all",  bucket: "50-69", delta_pp:  -4.2, hit_rate: 55.3, decided: 199, hits: 110, ok: true  },
      { sport: "NBA",  bucket: "70-89", delta_pp: -28.1, hit_rate: 51.4, decided: 37,  hits: 19,  ok: false },
      { sport: "NBA",  bucket: "50-69", delta_pp:   2.3, hit_rate: 61.8, decided: 76,  hits: 47,  ok: true  },
      { sport: "MLB",  bucket: "70-89", delta_pp: -22.5, hit_rate: 57.0, decided: 50,  hits: 28,  ok: false },
      { sport: "MLB",  bucket: "50-69", delta_pp:  -8.0, hit_rate: 51.5, decided: 109, hits: 56,  ok: true  },
    ],
    cohorts: {},
    generated_at: new Date().toISOString(),
  };
}

function mockBets(): BetsResponse {
  return {
    total: 0, returned: 0, bets: [],
    generated_at: new Date().toISOString(),
  };
}

function mockCostSpend(): CostSpendSnapshot {
  const today = new Date().toISOString().slice(0, 10);
  return {
    today,
    today_cost_usd: 0,
    avg_7d_usd: 0,
    delta_vs_7d_usd: 0,
    projection_month_usd: 0,
    days_remaining_in_month: 0,
    cache_hit_rate: null,
    window_total: {
      days_in_window: 0, requests: 0, cost_usd: 0,
      input_tokens: 0, output_tokens: 0,
      cache_creation_input_tokens: 0, cache_read_input_tokens: 0,
    },
    series: [],
    generated_at: new Date().toISOString(),
  };
}

function mockPromptsIndex(): PromptIndex {
  return {
    generated_at: new Date().toISOString(),
    grader_version: null,
    prompts: [],
  };
}

function mockSnapshotsIndex(): SnapshotsIndex {
  return {
    sources: [], counts: {}, timestamps: [],
    earliest: null, latest: null, interval_minutes: 10,
    generated_at: new Date().toISOString(),
  };
}

function mockHrRotation(): HrRotation {
  return {
    total_competitions: 0,
    sports_seen: [],
    daily_series: [],
    longevity_buckets: {},
    recent_rotations: [],
    bet_id_files: 0,
    generated_at: new Date().toISOString(),
  };
}

function mockAnomalyEvents(): AnomalyEventLog {
  return { events: [], total: 0 };
}

function mockTodos(): TodosIndex {
  return { total: 0, by_marker: {}, by_repo: {}, todos: [] };
}
